import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, LogOut, CreditCard, Search, MapPin, TrendingDown } from 'lucide-react';
import InsuranceForm from '@/components/InsuranceForm';
import EstimateForm from '@/components/EstimateForm';
import ComparisonView from '@/components/ComparisonView';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user, token, onLogout }) {
  const [activeView, setActiveView] = useState('estimate');
  const [insurance, setInsurance] = useState(null);
  const [hasSeeded, setHasSeeded] = useState(false);

  useEffect(() => {
    loadInsurance();
    seedData();
  }, []);

  const seedData = async () => {
    if (!hasSeeded) {
      try {
        await axios.post(`${API}/seed-data`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasSeeded(true);
      } catch (error) {
        // Ignore errors - data might already be seeded
      }
    }
  };

  const loadInsurance = async () => {
    try {
      const response = await axios.get(`${API}/insurance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.length > 0) {
        setInsurance(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading insurance:', error);
    }
  };

  const handleInsuranceUpdate = (updatedInsurance) => {
    setInsurance(updatedInsurance);
    toast.success('Insurance information saved!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_health-cost-finder/artifacts/kj0ydf6o_HCD-1.png"
                alt="Healthcare Dollar Logo"
                className="h-12 w-auto"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-700">{user?.username}</p>
                <p className="text-xs text-gray-500">Patient Account</p>
              </div>
              <Button
                onClick={onLogout}
                data-testid="logout-button"
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Hero Section */}
        <Card className="mb-6 border-0 shadow-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white overflow-hidden">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <CardContent className="py-8 px-6">
              <h2 className="text-3xl font-bold mb-3">Welcome back, {user?.username}!</h2>
              <p className="text-lg text-green-50 mb-4">
                Healthcare Dollar puts you in control — before the bill arrives.
              </p>
              <div className="space-y-2 text-sm text-green-50">
                <p className="flex items-start gap-2">
                  <span className="text-green-200">✓</span>
                  <span>We believe clarity is a right, not a privilege</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-green-200">✓</span>
                  <span>Designed to close gaps, not widen them</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-green-200">✓</span>
                  <span>Your platform for financial transparency</span>
                </p>
              </div>
            </CardContent>
            <div className="hidden md:block relative h-64 md:h-full">
              <img 
                src="https://customer-assets.emergentagent.com/job_health-cost-finder/artifacts/m2emq3ly_image.png"
                alt="Healthcare Professional"
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="eager"
                style={{ imageRendering: 'high-quality' }}
              />
            </div>
          </div>
        </Card>
        
        {/* Feature Highlight - Insurance-Specific Pricing */}
        {insurance && (
          <Card className="mb-6 border-0 shadow-lg bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg">Personalized to Your {insurance.carrier_name} Plan</h3>
                  <p className="text-sm text-blue-50">
                    Your costs are calculated using YOUR insurance's negotiated rates with each provider. Different carriers pay different amounts - we show YOUR actual costs.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="flex gap-2 bg-white/60 backdrop-blur-sm p-2 rounded-2xl border border-gray-200 inline-flex">
            <Button
              data-testid="estimate-tab"
              onClick={() => setActiveView('estimate')}
              variant={activeView === 'estimate' ? 'default' : 'ghost'}
              className={`gap-2 rounded-xl transition-all ${
                activeView === 'estimate'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                  : 'hover:bg-white/50'
              }`}
            >
              <Search className="w-4 h-4" />
              Get Estimate
            </Button>
            <Button
              data-testid="insurance-tab"
              onClick={() => setActiveView('insurance')}
              variant={activeView === 'insurance' ? 'default' : 'ghost'}
              className={`gap-2 rounded-xl transition-all ${
                activeView === 'insurance'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                  : 'hover:bg-white/50'
              }`}
            >
              <CreditCard className="w-4 h-4" />
              My Insurance
            </Button>
          </div>
        </div>

        {/* Content Area */}
        {!insurance && activeView === 'estimate' ? (
          <Card className="border-0 shadow-xl bg-gradient-to-br from-blue-50 to-cyan-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Add Insurance Information First
              </CardTitle>
              <CardDescription>
                To get accurate cost estimates, please add your insurance information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                data-testid="add-insurance-button"
                onClick={() => setActiveView('insurance')}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
              >
                Add Insurance Information
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {activeView === 'estimate' && (
              <div className="space-y-6">
                <EstimateForm token={token} insurance={insurance} />
              </div>
            )}

            {activeView === 'insurance' && (
              <InsuranceForm
                token={token}
                insurance={insurance}
                onUpdate={handleInsuranceUpdate}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}